import React, { useEffect, useState } from "react";
import API from "../api";
import dayjs from "dayjs";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
  FaClock,
  FaCalendarCheck,
  FaHourglassHalf,
  FaDollarSign,
  FaUser,
  FaChartPie,
} from "react-icons/fa";
import "./EmployeeWorkingHours.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const DAYS_ORDER = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

function fmt12(time24) {
  if (!time24) return "—";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

const TIMEFRAMES = [
  { key: "week",   label: "This Week" },
  { key: "month",  label: "This Month" },
  { key: "custom", label: "Custom" },
];

function EmployeeWorkingHours() {
  const [employees,        setEmployees]        = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeframe,        setTimeframe]        = useState("week");
  const [dateRef]                               = useState(dayjs());
  const [customStart,      setCustomStart]      = useState("");
  const [customEnd,        setCustomEnd]        = useState("");
  const [result,           setResult]           = useState(null);
  const [loading,          setLoading]          = useState(false);

  const user       = JSON.parse(localStorage.getItem("user"));
  const businessId = user?.user?.business_id || user?.business_id || user?.id;

  useEffect(() => {
    if (!businessId) return;
    API.get(`/auth/employees/business/${businessId}`)
      .then((res) => setEmployees(res.data || []))
      .catch((e) => console.error("Failed to load employees", e));
  }, [businessId]);

  // Reset result whenever controls change
  useEffect(() => { setResult(null); }, [selectedEmployee, timeframe, customStart, customEnd]);

  const getServicePrice = async (serviceId, cache) => {
    if (!serviceId) return 0;
    if (!cache[serviceId]) {
      try {
        const res = await API.get(`/auth/getServiceById?serviceId=${serviceId}`);
        cache[serviceId] = res.data?.price || 0;
      } catch {
        cache[serviceId] = 0;
      }
    }
    return cache[serviceId];
  };

  const calculateHours = async () => {
    if (!selectedEmployee) return;
    if (timeframe === "custom" && (!customStart || !customEnd)) return;
    setLoading(true);

    const { workingDays = [], workingHours = {}, breaks = {}, _id } = selectedEmployee;
    let totalAvailable = 0;

    const start =
      timeframe === "week"   ? dateRef.startOf("week")
      : timeframe === "month"  ? dateRef.startOf("month")
      : dayjs(customStart);

    const end =
      timeframe === "week"   ? dateRef.endOf("week")
      : timeframe === "month"  ? dateRef.endOf("month")
      : dayjs(customEnd).endOf("day");

    for (let d = start; d.isBefore(end) || d.isSame(end, "day"); d = d.add(1, "day")) {
      const dayName = d.format("dddd");
      if (!workingDays.includes(dayName)) continue;
      const hours = workingHours[dayName];
      if (!hours) continue;
      const open  = dayjs(`${d.format("YYYY-MM-DD")}T${hours.opensAt}`);
      const close = dayjs(`${d.format("YYYY-MM-DD")}T${hours.closesAt}`);
      if (!open.isValid() || !close.isValid()) continue;
      let duration = close.diff(open, "minute");
      const dayBreaks = (breaks?.[dayName] || []).map((b) => ({
        start: dayjs(`${d.format("YYYY-MM-DD")}T${b.start}`),
        end:   dayjs(`${d.format("YYYY-MM-DD")}T${b.end}`),
      }));
      for (const brk of dayBreaks) {
        if (brk.start.isValid() && brk.end.isValid()) {
          duration -= brk.end.diff(brk.start, "minute");
        }
      }
      if (duration > 0) totalAvailable += duration;
    }

    const bookingsRes = await API.get(`/auth/getAllBookings?employeeId=${_id}`);
    const bookings = bookingsRes.data || [];

    const relevantBookings = bookings.filter((b) => {
      const bDate = dayjs(b.bookingDateTime);
      return bDate.isAfter(start) && bDate.isBefore(end);
    });

    const servicePriceCache = {};
    let totalRevenue = 0;
    for (const booking of relevantBookings) {
      const mainPrice = await getServicePrice(booking.serviceId, servicePriceCache);
      let additionalPrice = 0;
      if (booking.additionalServices?.length) {
        const prices = await Promise.all(
          booking.additionalServices.map((id) => getServicePrice(id, servicePriceCache))
        );
        additionalPrice = prices.reduce((s, v) => s + v, 0);
      }
      totalRevenue += mainPrice + additionalPrice;
    }

    const totalBookedMinutes  = relevantBookings.length * 60;
    const totalAvailableHours = totalAvailable / 60;
    const totalBookedHours    = totalBookedMinutes / 60;
    const availableHours      = Math.max(totalAvailableHours - totalBookedHours, 0);

    setResult({ totalAvailable: totalAvailableHours, totalBooked: totalBookedHours, availableHours, totalRevenue });
    setLoading(false);
  };

  // Chart data
  const doughnutData = result
    ? {
        labels: ["Booked", "Available"],
        datasets: [{
          data: [
            parseFloat(result.totalBooked.toFixed(2)),
            parseFloat(result.availableHours.toFixed(2)),
          ],
          backgroundColor: ["#4f46e5", "#10b981"],
          borderColor:     ["#4338ca", "#059669"],
          borderWidth: 2,
          hoverOffset: 6,
        }],
      }
    : null;

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} hrs`,
        },
      },
    },
  };

  const utilizationPct =
    result && result.totalAvailable > 0
      ? Math.round((result.totalBooked / result.totalAvailable) * 100)
      : 0;

  // Weekly schedule rows derived from selected employee data
  const scheduleRows = selectedEmployee
    ? DAYS_ORDER.map((day) => ({
        day,
        isWorking: selectedEmployee.workingDays?.includes(day),
        hours:     selectedEmployee.workingHours?.[day],
        dayBreaks: selectedEmployee.breaks?.[day] || [],
      }))
    : [];

  const canCalculate =
    !!selectedEmployee &&
    !(timeframe === "custom" && (!customStart || !customEnd));

  return (
    <div className="ewh-page">

      {/* ── Page Header ── */}
      <div className="ewh-header">
        <h2 className="ewh-title">Employee Working Hours</h2>
        <p className="ewh-subtitle">Analyze availability, bookings, and revenue per employee</p>
      </div>

      {/* ── Controls ── */}
      <div className="ewh-controls-card">
        <div className="ewh-controls-row">

          <div className="ewh-control-group">
            <label className="ewh-label">Employee</label>
            <select
              className="ewh-select"
              value={selectedEmployee?._id || ""}
              onChange={(e) =>
                setSelectedEmployee(employees.find((emp) => emp._id === e.target.value) || null)
              }
            >
              <option value="">Select employee…</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>

          <div className="ewh-control-group">
            <label className="ewh-label">Timeframe</label>
            <div className="ewh-pills">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf.key}
                  className={`ewh-pill ${timeframe === tf.key ? "active" : ""}`}
                  onClick={() => setTimeframe(tf.key)}
                >
                  {tf.label}
                </button>
              ))}
            </div>
          </div>

          {timeframe === "custom" && (
            <div className="ewh-control-group">
              <label className="ewh-label">Date Range</label>
              <div className="ewh-date-row">
                <input
                  type="date"
                  className="ewh-date-input"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <span className="ewh-date-sep">→</span>
                <input
                  type="date"
                  className="ewh-date-input"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="ewh-control-group ewh-calc-group">
            <button
              className="ewh-calc-btn"
              onClick={calculateHours}
              disabled={loading || !canCalculate}
            >
              {loading ? "Calculating…" : "Calculate"}
            </button>
          </div>

        </div>
      </div>

      {/* ── Results ── */}
      {result && (
        <>
          {/* Stat cards */}
          <div className="ewh-stats-row">
            <div className="ewh-stat-card ewh-stat-blue">
              <div className="ewh-stat-icon"><FaClock /></div>
              <div className="ewh-stat-body">
                <div className="ewh-stat-label">Total Available</div>
                <div className="ewh-stat-value">
                  {result.totalAvailable.toFixed(1)} <span>hrs</span>
                </div>
              </div>
            </div>
            <div className="ewh-stat-card ewh-stat-indigo">
              <div className="ewh-stat-icon"><FaCalendarCheck /></div>
              <div className="ewh-stat-body">
                <div className="ewh-stat-label">Booked</div>
                <div className="ewh-stat-value">
                  {result.totalBooked.toFixed(1)} <span>hrs</span>
                </div>
              </div>
            </div>
            <div className="ewh-stat-card ewh-stat-green">
              <div className="ewh-stat-icon"><FaHourglassHalf /></div>
              <div className="ewh-stat-body">
                <div className="ewh-stat-label">Remaining</div>
                <div className="ewh-stat-value">
                  {result.availableHours.toFixed(1)} <span>hrs</span>
                </div>
              </div>
            </div>
            <div className="ewh-stat-card ewh-stat-amber">
              <div className="ewh-stat-icon"><FaDollarSign /></div>
              <div className="ewh-stat-body">
                <div className="ewh-stat-label">Revenue</div>
                <div className="ewh-stat-value">
                  ${result.totalRevenue.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="ewh-charts-row">

            {/* Doughnut */}
            <div className="ewh-chart-card">
              <div className="ewh-card-header">
                <FaChartPie className="ewh-card-icon" />
                <span>Hours Breakdown</span>
              </div>
              <div className="ewh-doughnut-outer">
                <div className="ewh-doughnut-wrap">
                  <Doughnut data={doughnutData} options={doughnutOptions} />
                  <div className="ewh-doughnut-center">
                    <div className="ewh-utilization-pct">{utilizationPct}%</div>
                    <div className="ewh-utilization-label">utilization</div>
                  </div>
                </div>
                <div className="ewh-doughnut-legend">
                  <div className="ewh-legend-item">
                    <span className="ewh-legend-dot" style={{ background: "#4f46e5" }} />
                    <span className="ewh-legend-name">Booked</span>
                    <span className="ewh-legend-val">{result.totalBooked.toFixed(1)} hrs</span>
                  </div>
                  <div className="ewh-legend-item">
                    <span className="ewh-legend-dot" style={{ background: "#10b981" }} />
                    <span className="ewh-legend-name">Available</span>
                    <span className="ewh-legend-val">{result.availableHours.toFixed(1)} hrs</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Utilization detail */}
            <div className="ewh-chart-card">
              <div className="ewh-card-header">
                <FaChartPie className="ewh-card-icon" />
                <span>Utilization Detail</span>
              </div>
              <div className="ewh-util-list">
                <div className="ewh-util-item">
                  <div className="ewh-util-head">
                    <span className="ewh-util-dot ewh-dot-indigo" />
                    <span className="ewh-util-name">Booked Hours</span>
                    <span className="ewh-util-hrs">{result.totalBooked.toFixed(1)} hrs</span>
                  </div>
                  <div className="ewh-progress-track">
                    <div
                      className="ewh-progress-fill ewh-fill-indigo"
                      style={{
                        width: `${result.totalAvailable > 0
                          ? Math.min((result.totalBooked / result.totalAvailable) * 100, 100)
                          : 0}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="ewh-util-item">
                  <div className="ewh-util-head">
                    <span className="ewh-util-dot ewh-dot-green" />
                    <span className="ewh-util-name">Available Hours</span>
                    <span className="ewh-util-hrs">{result.availableHours.toFixed(1)} hrs</span>
                  </div>
                  <div className="ewh-progress-track">
                    <div
                      className="ewh-progress-fill ewh-fill-green"
                      style={{
                        width: `${result.totalAvailable > 0
                          ? Math.min((result.availableHours / result.totalAvailable) * 100, 100)
                          : 0}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="ewh-util-item">
                  <div className="ewh-util-head">
                    <span className="ewh-util-dot ewh-dot-blue" />
                    <span className="ewh-util-name">Total Capacity</span>
                    <span className="ewh-util-hrs">{result.totalAvailable.toFixed(1)} hrs</span>
                  </div>
                  <div className="ewh-progress-track">
                    <div className="ewh-progress-fill ewh-fill-blue" style={{ width: "100%" }} />
                  </div>
                </div>

                <div className="ewh-revenue-display">
                  <div>
                    <div className="ewh-rev-label">Total Revenue</div>
                    <div className="ewh-rev-sub">for selected period</div>
                  </div>
                  <div className="ewh-rev-value">${result.totalRevenue.toFixed(2)}</div>
                </div>
              </div>
            </div>

          </div>
        </>
      )}

      {/* ── Weekly Schedule ── */}
      {selectedEmployee && (
        <div className="ewh-schedule-card">
          <div className="ewh-card-header">
            <FaUser className="ewh-card-icon" />
            <span>{selectedEmployee.name}'s Weekly Schedule</span>
          </div>
          <div className="ewh-schedule-grid">
            {scheduleRows.map(({ day, isWorking, hours, dayBreaks }) => (
              <div
                key={day}
                className={`ewh-day-card ${isWorking ? "ewh-day-active" : "ewh-day-off"}`}
              >
                <div className="ewh-day-name">{day.slice(0, 3).toUpperCase()}</div>
                {isWorking && hours ? (
                  <>
                    <div className="ewh-day-hours">
                      {fmt12(hours.opensAt)}
                      <br />
                      {fmt12(hours.closesAt)}
                    </div>
                    {dayBreaks.length > 0 && (
                      <div className="ewh-day-breaks">
                        {dayBreaks.map((b, i) => (
                          <span key={i} className="ewh-break-badge">
                            Break {fmt12(b.start)}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ewh-day-off-label">Off</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default EmployeeWorkingHours;
